/**
 * Main Contact synchronization manager
 * Coordinates bootstrap and incremental sync with Microsoft Graph
 */

import { ContactStore, MetaData } from './ContactStore';
import { CorporateSyncManager } from './sync/CorporateSyncManager';
import { PersonalSyncManager } from './sync/PersonalSyncManager';
import { InternalSyncManager } from './sync/InternalSyncManager';
import { AutoSyncManager } from './sync/AutoSyncManager';
import { SyncProgress, SyncOptions } from './sync/types';

// Re-export types for backward compatibility
export type { SyncProgress, SyncOptions } from './sync/types';

export class ContactSyncManager {
  private corporateSyncManager: CorporateSyncManager;
  private personalSyncManager: PersonalSyncManager;
  private internalSyncManager: InternalSyncManager;
  private autoSyncManager: AutoSyncManager;

  constructor(isOutlookContext: boolean = true) {
    this.corporateSyncManager = new CorporateSyncManager(isOutlookContext);
    this.personalSyncManager = new PersonalSyncManager(isOutlookContext);
    this.internalSyncManager = new InternalSyncManager(isOutlookContext);
    this.autoSyncManager = new AutoSyncManager(
      this.corporateSyncManager,
      this.personalSyncManager,
      this.internalSyncManager
    );
  }

  /**
   * Bootstrap sync - initial full download of all contacts using delta query
   */
  async bootstrapSync(
    accessToken: string, 
    sharedUpn: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    return this.corporateSyncManager.bootstrapSync(accessToken, sharedUpn, options);
  }

  /**
   * Incremental sync using delta tokens
   */
  async incrementalSync(
    accessToken: string,
    sharedUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    return this.corporateSyncManager.incrementalSync(accessToken, sharedUpn, options);
  }

  /**
   * Bootstrap sync for personal contacts - initial full download of user's own contacts
   */
  async bootstrapPersonalSync(
    accessToken: string, 
    userUpn: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    return this.personalSyncManager.bootstrapPersonalSync(accessToken, userUpn, options);
  }

  /**
   * Incremental sync for personal contacts using delta tokens
   */
  async incrementalPersonalSync(
    accessToken: string,
    userUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    return this.personalSyncManager.incrementalPersonalSync(accessToken, userUpn, options);
  }

  /**
   * Bootstrap sync for internal users - initial full download of organizational users
   */
  async bootstrapInternalSync(
    accessToken: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    return this.internalSyncManager.bootstrapInternalSync(accessToken, options);
  }

  /**
   * Incremental sync for internal users using delta tokens
   */
  async incrementalInternalSync(
    accessToken: string,
    options: SyncOptions = {}
  ): Promise<void> {
    return this.internalSyncManager.incrementalInternalSync(accessToken, options);
  }

  /**
   * Start automatic sync every 15 minutes
   */
  startAutoSync(accessToken: string, sharedUpn: string, getTokenCallback?: () => Promise<string>): void {
    return this.autoSyncManager.startAutoSync(accessToken, sharedUpn, getTokenCallback);
  }

  /**
   * Stop automatic sync
   */
  stopAutoSync(): void {
    return this.autoSyncManager.stopAutoSync();
  }

  /**
   * Start automatic sync for internal users every 15 minutes
   */
  startInternalAutoSync(accessToken: string, getTokenCallback?: () => Promise<string>): void {
    return this.autoSyncManager.startInternalAutoSync(accessToken, getTokenCallback);
  }

  /**
   * Stop automatic sync for internal users
   */
  stopInternalAutoSync(): void {
    return this.autoSyncManager.stopInternalAutoSync();
  }

  /**
   * Start automatic sync for personal contacts every configured interval
   */
  startPersonalDeltaSync(accessToken: string, userUpn: string, getTokenCallback?: () => Promise<string>): void {
    return this.autoSyncManager.startPersonalDeltaSync(accessToken, userUpn, getTokenCallback);
  }

  /**
   * Stop automatic sync for personal contacts
   */
  stopPersonalDeltaSync(): void {
    return this.autoSyncManager.stopPersonalDeltaSync();
  }

  /**
   * Get sync status
   */
  async getSyncStatus(): Promise<{
    isBootstrapComplete: boolean;
    lastFullSync: string | null;
    lastIncrementalSync: string | null;
    totalContacts: number;
    isSyncing: boolean;
    hasDeltaToken: boolean;
    isAutoSyncRunning: boolean;
    personalBootstrapComplete: boolean;
    personalLastFullSync: string | null;
    personalLastIncrementalSync: string | null;
    personalTotalContacts: number;
    personalHasDeltaToken: boolean;
    internalBootstrapComplete: boolean;
    internalLastFullSync: string | null;
    internalLastIncrementalSync: string | null;
    internalTotalUsers: number;
    internalHasDeltaToken: boolean;
    isInternalAutoSyncRunning: boolean;
  }> {
    const meta = await this.corporateSyncManager.getStore().getMeta();
    return {
      isBootstrapComplete: meta.bootstrapComplete,
      lastFullSync: meta.lastFullSync,
      lastIncrementalSync: meta.lastIncrementalSync,
      totalContacts: meta.totalContacts,
      isSyncing: this.corporateSyncManager.getIsSyncing() || this.personalSyncManager.getIsSyncing() || this.internalSyncManager.getIsSyncing(),
      hasDeltaToken: !!meta.deltaToken,
      isAutoSyncRunning: !!this.autoSyncManager.syncInterval,
      personalBootstrapComplete: meta.personalBootstrapComplete,
      personalLastFullSync: meta.personalLastFullSync,
      personalLastIncrementalSync: meta.personalLastIncrementalSync,
      personalTotalContacts: meta.personalTotalContacts,
      personalHasDeltaToken: !!meta.personalDeltaToken,
      internalBootstrapComplete: meta.internalBootstrapComplete,
      internalLastFullSync: meta.internalLastFullSync,
      internalLastIncrementalSync: meta.internalLastIncrementalSync,
      internalTotalUsers: meta.internalTotalUsers,
      internalHasDeltaToken: !!meta.internalDeltaToken,
      isInternalAutoSyncRunning: !!this.autoSyncManager.internalSyncInterval,
    };
  }

  /**
   * Debug method to log current metadata state
   */
  async debugMetadata(): Promise<void> {
    const meta = await this.corporateSyncManager.getStore().getMeta();
    console.log('Current metadata state:', {
      bootstrapComplete: meta.bootstrapComplete,
      lastFullSync: meta.lastFullSync,
      lastIncrementalSync: meta.lastIncrementalSync,
      totalContacts: meta.totalContacts,
      hasDeltaToken: !!meta.deltaToken,
      deltaTokenPreview: meta.deltaToken ? meta.deltaToken.substring(0, 100) + '...' : 'null',
      personalBootstrapComplete: meta.personalBootstrapComplete,
      personalLastFullSync: meta.personalLastFullSync,
      personalLastIncrementalSync: meta.personalLastIncrementalSync,
      personalTotalContacts: meta.personalTotalContacts,
      personalHasDeltaToken: !!meta.personalDeltaToken,
      personalDeltaTokenPreview: meta.personalDeltaToken ? meta.personalDeltaToken.substring(0, 100) + '...' : 'null',
      internalBootstrapComplete: meta.internalBootstrapComplete,
      internalLastFullSync: meta.internalLastFullSync,
      internalLastIncrementalSync: meta.internalLastIncrementalSync,
      internalTotalUsers: meta.internalTotalUsers,
      internalHasDeltaToken: !!meta.internalDeltaToken,
      internalDeltaTokenPreview: meta.internalDeltaToken ? meta.internalDeltaToken.substring(0, 100) + '...' : 'null',
    });
  }

  /**
   * Force full resync (clears local data and re-bootstraps)
   */
  async forceResync(
    accessToken: string,
    sharedUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    await this.corporateSyncManager.getStore().clearAllData();
    await this.bootstrapSync(accessToken, sharedUpn, options);
  }

  /**
   * Force full resync for personal contacts (clears personal data and re-bootstraps)
   */
  async forcePersonalResync(
    accessToken: string,
    userUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    // Clear only personal contacts data
    const meta = await this.personalSyncManager.getStore().getMeta();
    const updatedMeta: MetaData = {
      ...meta,
      personalBootstrapComplete: false,
      personalLastFullSync: null,
      personalLastIncrementalSync: null,
      personalDeltaToken: null,
      personalTotalContacts: 0,
    };
    await this.personalSyncManager.getStore().setMeta(updatedMeta);
    
    // Remove personal contacts from the store
    const allContacts = await this.personalSyncManager.getStore().getAllContacts();
    const personalContacts = allContacts.filter(contact => contact.sourceType === 'personal');
    if (personalContacts.length > 0) {
      await this.personalSyncManager.getStore().bulkMarkContactsDeleted(personalContacts.map(contact => contact.id));
    }
    
    // Bootstrap personal contacts
    await this.bootstrapPersonalSync(accessToken, userUpn, options);
  }

  /**
   * Reset metadata to force bootstrap sync on next attempt
   */
  async resetMetadata(): Promise<void> {
    const meta: MetaData = {
      schemaVersion: 1,
      lastFullSync: null,
      lastIncrementalSync: null,
      deltaToken: null,
      changeToken: null,
      bootstrapComplete: false,
      totalContacts: 0,
      personalLastFullSync: null,
      personalLastIncrementalSync: null,
      personalDeltaToken: null,
      personalBootstrapComplete: false,
      personalTotalContacts: 0,
      internalLastFullSync: null,
      internalLastIncrementalSync: null,
      internalDeltaToken: null,
      internalBootstrapComplete: false,
      internalTotalUsers: 0,
    };
    await this.corporateSyncManager.getStore().setMeta(meta);
    console.log('Metadata reset - next sync will be a bootstrap sync');
  }

  /**
   * Reset sync state to allow new sync attempts
   */
  resetSyncState(): void {
    // Note: We can't directly access protected properties, so we'll reset through the auto sync manager
    this.autoSyncManager.stopAutoSync();
    this.autoSyncManager.stopInternalAutoSync();
    console.log('Sync state reset');
  }

  /**
   * Manually trigger auto-sync (for testing)
   */
  async triggerAutoSync(accessToken: string, sharedUpn: string): Promise<void> {
    return this.autoSyncManager.triggerAutoSync(accessToken, sharedUpn);
  }

  /**
   * Check if incremental sync is needed and perform it if necessary
   */
  async checkAndPerformStartupSync(
    accessToken: string, 
    sharedUpn: string, 
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    return this.autoSyncManager.checkAndPerformStartupSync(accessToken, sharedUpn, getTokenCallback);
  }

  /**
   * Check if personal contacts incremental sync is needed and perform it if necessary
   */
  async checkAndPerformPersonalStartupSync(
    accessToken: string, 
    userUpn: string, 
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    return this.autoSyncManager.checkAndPerformPersonalStartupSync(accessToken, userUpn, getTokenCallback);
  }

  /**
   * Check if internal users incremental sync is needed and perform it if necessary
   */
  async checkAndPerformInternalStartupSync(
    accessToken: string,
    getTokenCallback?: () => Promise<string>
  ): Promise<boolean> {
    return this.autoSyncManager.checkAndPerformInternalStartupSync(accessToken, getTokenCallback);
  }

  /**
   * Parallel bootstrap sync - runs corporate, personal, and internal syncs simultaneously
   */
  async parallelBootstrapSync(
    accessToken: string,
    sharedUpn: string,
    userUpn: string, 
    options: SyncOptions = {}
  ): Promise<void> {
    const { onProgress, onError } = options;
    
    console.log('Starting parallel bootstrap sync...');
    
    // Update progress to show parallel sync starting
      onProgress?.({
        current: 0,
      total: 3,
        phase: 'bootstrap',
      message: 'Starting parallel sync of all contact sources...'
    });

    try {
      // Create options without search index building and delta token generation for individual syncs
      const syncOptionsWithoutIndex: SyncOptions = {
        ...options,
        skipSearchIndex: true,
        skipDeltaToken: true // Skip delta token generation for faster sync
      };

      // Run all syncs in parallel
      const syncPromises = [
        this.corporateSyncManager.bootstrapSync(accessToken, sharedUpn, syncOptionsWithoutIndex),
        this.personalSyncManager.bootstrapPersonalSync(accessToken, userUpn, syncOptionsWithoutIndex),
        this.internalSyncManager.bootstrapInternalSync(accessToken, syncOptionsWithoutIndex)
      ];

      // Wait for all syncs to complete
      await Promise.all(syncPromises);

      onProgress?.({
        current: 3,
        total: 3,
        phase: 'search-index',
        message: 'All syncs complete, building unified search index...'
      });
      
      // Build search index once after all syncs are complete
      await this.buildUnifiedSearchIndex(options);

      onProgress?.({
        current: 3,
        total: 3,
        phase: 'bootstrap',
        message: 'Parallel bootstrap sync complete!'
      });

      console.log('Parallel bootstrap sync completed successfully');

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Parallel bootstrap sync failed');
      console.error('Parallel bootstrap sync failed:', err);
      onError?.(err);
      throw err;
    }
  }

  /**
   * Parallel incremental sync - runs corporate, personal, and internal incremental syncs simultaneously
   */
  async parallelIncrementalSync(
    accessToken: string,
    sharedUpn: string,
    userUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    const { onProgress, onError } = options;

    console.log('Starting parallel incremental sync...');
    
    // Update progress to show parallel sync starting
      onProgress?.({
        current: 0,
      total: 3,
        phase: 'incremental',
      message: 'Starting parallel incremental sync...'
    });

    try {
      // Create options without search index building and delta token generation for individual syncs
      const syncOptionsWithoutIndex: SyncOptions = {
        ...options,
        skipSearchIndex: true,
        skipDeltaToken: true // Skip delta token generation for faster sync
      };

      // Run all incremental syncs in parallel
      const syncPromises = [
        this.corporateSyncManager.incrementalSync(accessToken, sharedUpn, syncOptionsWithoutIndex),
        this.personalSyncManager.incrementalPersonalSync(accessToken, userUpn, syncOptionsWithoutIndex),
        this.internalSyncManager.incrementalInternalSync(accessToken, syncOptionsWithoutIndex)
      ];

      // Wait for all syncs to complete
      await Promise.all(syncPromises);

        onProgress?.({
        current: 3,
        total: 3,
          phase: 'search-index',
        message: 'All incremental syncs complete, building unified search index...'
      });

      // Build search index once after all syncs are complete
      await this.buildUnifiedSearchIndex(options);

      onProgress?.({
        current: 3,
        total: 3,
        phase: 'incremental',
        message: 'Parallel incremental sync complete!'
      });

      console.log('Parallel incremental sync completed successfully');

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Parallel incremental sync failed');
      console.error('Parallel incremental sync failed:', err);
      onError?.(err);
      throw err;
    }
  }

  /**
   * Optimized smart parallel sync - bootstrap + immediate index + background delta
   */
  async smartParallelSync(
    accessToken: string,
    sharedUpn: string,
    userUpn: string,
    options: SyncOptions = {}
  ): Promise<void> {
    const { onProgress, onError } = options;
    
    console.log('Starting optimized smart parallel sync...');
    
    try {
      const meta = await this.corporateSyncManager.getStore().getMeta();
      
      // Determine which syncs need bootstrap vs incremental
      const needsBootstrap = {
        corporate: options.forceBootstrap || !meta.bootstrapComplete,
        personal: options.forceBootstrap || !meta.personalBootstrapComplete,
        internal: options.forceBootstrap || !meta.internalBootstrapComplete
      };

      console.log('Sync requirements:', needsBootstrap);

      // Clear all data if forceBootstrap is true to ensure complete refresh
      if (options.forceBootstrap) {
        console.log('Force bootstrap requested - clearing all contact data...');
        await this.corporateSyncManager.getStore().clearAllData();
        await this.personalSyncManager.getStore().clearAllData();
        await this.internalSyncManager.getStore().clearAllData();
        console.log('All contact data cleared for forced bootstrap sync');
      }

      // Phase 1: Parallel sync + immediate index
      const hasAnyBootstrap = needsBootstrap.corporate || needsBootstrap.personal || needsBootstrap.internal;
      const isForcedBootstrap = options.forceBootstrap;
      onProgress?.({
        current: 0,
        total: 3,
        phase: hasAnyBootstrap ? 'bootstrap' : 'incremental',
        message: isForcedBootstrap ? 'Downloading all contacts in parallel...' : 
                hasAnyBootstrap ? 'Downloading contacts in parallel...' : 'Updating contacts in parallel...'
      });

      // Create options without search index building and delta token generation for individual syncs
      const syncOptionsWithoutIndex: SyncOptions = {
        ...options,
        skipSearchIndex: true,
        skipDeltaToken: true // Skip delta token generation for faster sync
      };

      // Run appropriate syncs in parallel based on bootstrap status
      const syncPromises = [];
      
      if (needsBootstrap.corporate) {
        console.log('Corporate: Bootstrap sync needed');
        syncPromises.push(
          this.corporateSyncManager.bootstrapSync(accessToken, sharedUpn, syncOptionsWithoutIndex)
        );
      } else {
        console.log('Corporate: Incremental sync needed');
        syncPromises.push(
          this.corporateSyncManager.incrementalSync(accessToken, sharedUpn, syncOptionsWithoutIndex)
        );
      }
      
      if (needsBootstrap.personal) {
        console.log('Personal: Bootstrap sync needed');
        syncPromises.push(
          this.personalSyncManager.bootstrapPersonalSync(accessToken, userUpn, syncOptionsWithoutIndex)
        );
      } else {
        console.log('Personal: Incremental sync needed');
        syncPromises.push(
          this.personalSyncManager.incrementalPersonalSync(accessToken, userUpn, syncOptionsWithoutIndex)
        );
      }
      
      if (needsBootstrap.internal) {
        console.log('Internal: Bootstrap sync needed');
        syncPromises.push(
          this.internalSyncManager.bootstrapInternalSync(accessToken, syncOptionsWithoutIndex)
        );
      } else {
        console.log('Internal: Incremental sync needed');
        syncPromises.push(
          this.internalSyncManager.incrementalInternalSync(accessToken, syncOptionsWithoutIndex)
        );
      }

      // Wait for all syncs to complete
      await Promise.all(syncPromises);

      // Build search index immediately
      onProgress?.({
        current: 3,
        total: 3,
        phase: 'search-index',
        message: 'Building search index...'
      });
      
      await this.buildUnifiedSearchIndex(options);

      // Clear progress UI - bootstrap and indexing are complete
      onProgress?.({
        current: 3,
        total: 3,
        phase: 'complete',
        message: 'Contacts ready!'
      });

      // Phase 2: Generate delta tokens in background (silently, no progress updates)
      this.generateDeltaTokensInBackground(accessToken, sharedUpn, userUpn);

      // Phase 3: Start background delta sync (silently, no progress updates)
      this.startBackgroundDeltaSync(accessToken, sharedUpn, userUpn);

      console.log('Optimized smart parallel sync completed successfully');

    } catch (error) {
      const err = error instanceof Error ? error : new Error('Optimized smart parallel sync failed');
      console.error('Optimized smart parallel sync failed:', err);
      onError?.(err);
      throw err;
    }
  }

  /**
   * Start background delta sync with staggered timers
   */
  private startBackgroundDeltaSync(
    accessToken: string,
    sharedUpn: string,
    userUpn: string
  ): void {
    console.log('Starting background delta sync with staggered timers...');
    
    // Start delta sync timers with staggered delays to prevent API rate limiting
    setTimeout(() => {
      console.log('Starting corporate delta sync timer (2s delay)...');
      this.startAutoSync(accessToken, sharedUpn);
    }, 2000); // Corporate delta sync after 2s
    
    setTimeout(() => {
      console.log('Starting personal delta sync timer (3s delay)...');
      this.startPersonalDeltaSync(accessToken, userUpn);
    }, 3000); // Personal delta sync after 3s
    
    setTimeout(() => {
      console.log('Starting internal delta sync timer (4s delay)...');
      this.startInternalAutoSync(accessToken);
    }, 4000); // Internal delta sync after 4s
    
    console.log('Background delta sync timers scheduled');
  }

  /**
   * Build unified search index after parallel sync operations
   */
  private async buildUnifiedSearchIndex(options: SyncOptions): Promise<void> {
    const { onProgress } = options;
    
    console.log('Building unified search index after parallel sync...');
      onProgress?.({
        current: 0,
        total: 0,
          phase: 'search-index',
      message: 'Building unified search index...'
    });
    
    // Use the corporate sync manager's search index manager (they all share the same store)
    await this.corporateSyncManager.getSearchIndexManager().buildIndex();
    console.log('Unified search index built successfully');
  }

  /**
   * Generate delta tokens in the background for all sources
   */
  private async generateDeltaTokensInBackground(
    accessToken: string,
    sharedUpn: string,
    userUpn: string
  ): Promise<void> {
    console.log('Starting background delta token generation...');
    
    try {
      // Generate delta tokens for all sources in parallel
      const deltaTokenPromises = [
        this.corporateSyncManager.generateDeltaToken(accessToken, sharedUpn),
        this.personalSyncManager.generateDeltaToken(accessToken, userUpn),
        this.internalSyncManager.generateDeltaToken(accessToken)
      ];
      
      await Promise.all(deltaTokenPromises);
      console.log('Background delta token generation completed');
    } catch (error) {
      console.warn('Background delta token generation failed:', error);
    }
  }

  /**
   * Get the contact store instance
   */
  getStore(): ContactStore {
    return this.corporateSyncManager.getStore();
  }

  /**
   * Check auto-sync status and log details
   */
  checkAutoSyncStatus(): void {
    this.autoSyncManager.checkAutoSyncStatus();
  }

  /**
   * Fast startup sync that only runs delta sync if sync interval has expired
   */
  async fastStartupSync(
    accessToken: string,
    sharedUpn: string,
    userUpn: string
  ): Promise<void> {
    console.log('Starting fast startup sync...');
    
    try {
      const meta = await this.corporateSyncManager.getStore().getMeta();
      const syncIntervalMs = this.autoSyncManager.getSyncIntervalMs();
      
      // Check if delta sync is needed based on sync interval
      const deltaSyncNeeded = {
        corporate: this.isDeltaSyncNeeded(meta.lastIncrementalSync, meta.lastFullSync, syncIntervalMs),
        personal: this.isDeltaSyncNeeded(meta.personalLastIncrementalSync, meta.personalLastFullSync, syncIntervalMs),
        internal: this.isDeltaSyncNeeded(meta.internalLastIncrementalSync, meta.internalLastFullSync, syncIntervalMs)
      };
      
      console.log('Delta sync needed based on interval:', deltaSyncNeeded);
      
      // If no delta sync needed, just start auto-sync timers
      if (!deltaSyncNeeded.corporate && !deltaSyncNeeded.personal && !deltaSyncNeeded.internal) {
        console.log('No delta sync needed - starting auto-sync timers only');
        this.startBackgroundDeltaSync(accessToken, sharedUpn, userUpn);
        return;
      }
      
      // Some sources need delta sync - run silently in background
      console.log('Running background delta sync for expired sources...');
      await this.runBackgroundDeltaSyncSilently(accessToken, sharedUpn, userUpn, deltaSyncNeeded);
      
      // Start auto-sync timers
      this.startBackgroundDeltaSync(accessToken, sharedUpn, userUpn);
      
    } catch (error) {
      console.error('Fast startup sync failed:', error);
      // Fallback to regular startup sync
      await this.smartParallelSync(accessToken, sharedUpn, userUpn, {});
    }
  }

  /**
   * Check if delta sync is needed based on sync interval
   */
  private isDeltaSyncNeeded(lastIncrementalSync: string | null, lastFullSync: string | null, syncIntervalMs: number): boolean {
    // Determine the most recent sync time
    let lastSyncTime: number | null = null;
    
    if (lastIncrementalSync && lastFullSync) {
      const incrementalTime = new Date(lastIncrementalSync).getTime();
      const fullTime = new Date(lastFullSync).getTime();
      lastSyncTime = Math.max(incrementalTime, fullTime);
    } else if (lastIncrementalSync) {
      lastSyncTime = new Date(lastIncrementalSync).getTime();
    } else if (lastFullSync) {
      lastSyncTime = new Date(lastFullSync).getTime();
    }
    
    if (!lastSyncTime) return true; // No sync history, need delta sync
    
    const now = Date.now();
    const timeSinceLastSync = now - lastSyncTime;
    
    return timeSinceLastSync >= syncIntervalMs;
  }

  /**
   * Run background delta sync silently for sources that need it
   */
  private async runBackgroundDeltaSyncSilently(
    accessToken: string, 
    sharedUpn: string,
    userUpn: string,
    deltaSyncNeeded: { corporate: boolean; personal: boolean; internal: boolean }
  ): Promise<void> {
    console.log('Running background delta sync silently...');
    
    try {
      const syncPromises = [];
      
      if (deltaSyncNeeded.corporate) {
        syncPromises.push(
          this.corporateSyncManager.incrementalSync(accessToken, sharedUpn, { skipSearchIndex: true })
        );
      }
      
      if (deltaSyncNeeded.personal) {
        syncPromises.push(
          this.personalSyncManager.incrementalPersonalSync(accessToken, userUpn, { skipSearchIndex: true })
        );
      }
      
      if (deltaSyncNeeded.internal) {
        syncPromises.push(
          this.internalSyncManager.incrementalInternalSync(accessToken, { skipSearchIndex: true })
        );
      }
      
      if (syncPromises.length > 0) {
        await Promise.all(syncPromises);
        
        // Rebuild search index once after all syncs
        await this.buildUnifiedSearchIndex({});
      }
      
      console.log('Background delta sync completed silently');
      
    } catch (error) {
      console.warn('Background delta sync failed:', error);
    }
  }

  /**
   * Manually start auto-sync (for testing)
   */
  manualStartAutoSync(accessToken: string, sharedUpn: string, getTokenCallback?: () => Promise<string>): void {
    this.startAutoSync(accessToken, sharedUpn, getTokenCallback);
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.autoSyncManager.destroy();
    this.corporateSyncManager.destroy();
    this.personalSyncManager.destroy();
    this.internalSyncManager.destroy();
  }
}
